import { UserRole } from "constants/userRole";
import { Button } from "primereact/button";
import { useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useGetMyTrainingQuery } from "store/queries/training";
import { userSelector } from "store/slices/user";
import styled from "styled-components";

/** sessionStorage key: the banner stays hidden for the rest of this browser session once dismissed. */
export const TRAINING_BANNER_DISMISSED_KEY = "clearCertified.bannerDismissed";

/**
 * A full-width band across the top of the hospital view (dev feedback round 1: the old pale box read as
 * an alert inside the page). The negative margins cancel the app layout's content gutter
 * (`--content-gutter`, both sides) and its top gap (`--content-gap-top`), so the band runs edge to edge
 * directly under the fixed tab bar. Brand purple, white text, like the app's other prominent surfaces.
 */
const Banner = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin: calc(-1 * var(--content-gap-top, 0px))
    calc(-1 * var(--content-gutter, 0px)) 16px;
  padding: 10px 16px 10px 24px;
  background: var(--brand-purple-700);
  color: var(--brand-white);
  font-size: 0.9rem;
  font-weight: var(--font-weight-semibold);

  .pi-verified {
    font-size: 1.25rem;
  }
`;

const Text = styled.span`
  flex: 1;
`;

const StartButton = styled(Button)`
  &.p-button {
    background: var(--brand-white);
    border-color: var(--brand-white);
    color: var(--brand-purple-700);
    font-weight: var(--font-weight-semibold);
  }

  &.p-button:enabled:hover {
    background: var(--brand-blue-25);
    border-color: var(--brand-blue-25);
    color: var(--brand-purple-700);
  }
`;

const DismissButton = styled(Button)`
  &.p-button.p-button-text {
    color: var(--brand-white);
  }

  &.p-button.p-button-text:enabled:hover {
    background: rgba(255, 255, 255, 0.15);
    color: var(--brand-white);
  }
`;

function readDismissed(): boolean {
  try {
    return window.sessionStorage.getItem(TRAINING_BANNER_DISMISSED_KEY) === "1";
  } catch {
    return false;
  }
}

function writeDismissed() {
  try {
    window.sessionStorage.setItem(TRAINING_BANNER_DISMISSED_KEY, "1");
  } catch {
    // Storage blocked (private window etc.): the banner still hides until the page reloads.
  }
}

interface ITrainingBannerProps {
  /** Overrides the Start / Resume action. PR 2 wires this to the tour. */
  onStart?: () => void;
}

/**
 * CLE-3015 Clear Certified — "Get Clear Certified" nag on the hospital view.
 *
 * Shown only when all of: the current account has `features.clearCertifiedTraining` on (the API's
 * `enabled`), the user is HospitalStaff (never a SuperAdmin), and the user is not certified yet.
 * Nothing is gated behind it. Dismissing hides it for this browser session only.
 *
 * Until the tour exists (PR 2), Start / Resume opens the hospital's Estimates tab with
 * `?training=1`, the parameter PR 2 reads to start the tour.
 */
export function TrainingBanner({ onStart }: ITrainingBannerProps) {
  const navigate = useNavigate();
  const { scope, currentAccount } = useSelector(userSelector);
  const isHospitalStaff = scope === UserRole.HOSPITAL_STAFF;

  const { data } = useGetMyTrainingQuery(currentAccount, {
    skip: !isHospitalStaff || !currentAccount,
  });
  const [dismissed, setDismissed] = useState<boolean>(readDismissed);

  const record = data?.data;
  if (!isHospitalStaff || !record?.enabled || record.clearCertifiedAt) {
    return null;
  }
  if (dismissed) {
    return null;
  }

  const resuming = Boolean(record.trainingProgress?.currentStepId);

  const handleStart = () => {
    if (onStart) {
      onStart();
      return;
    }
    navigate(`/hospitals/view/${currentAccount}/orders?training=1`);
  };

  const handleDismiss = () => {
    writeDismissed();
    setDismissed(true);
  };

  return (
    <Banner role="region" aria-label="Clear Certified training">
      <i className="pi pi-verified" aria-hidden />
      <Text>Get Clear Certified: a 20-minute walk-through of the app.</Text>
      <StartButton
        type="button"
        className="p-button-sm p-button-rounded-md"
        label={resuming ? "Resume" : "Start"}
        onClick={handleStart}
      />
      <DismissButton
        type="button"
        icon="pi pi-times"
        className="p-button-text p-button-rounded p-button-sm"
        aria-label="Dismiss"
        onClick={handleDismiss}
      />
    </Banner>
  );
}
